// Story 10.3 — Detalle de la conversación seleccionada.

import { ArrowLeft, Loader2, Play } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useConversation } from '@/hooks/queries/use-conversation';
import { useConversationRealtime } from '@/hooks/use-conversation-realtime';
import {
  type ConversationMessage,
  deriveDisplayName,
  findReplySelection,
} from '@/lib/conversations-types';
import { cn } from '@/lib/utils';
import { MessageBubble } from './MessageBubble';
import { ReplayPlayer } from './ReplayPlayer';
import { SessionPanel } from './SessionPanel';

interface Props {
  botId: number;
  sessionId: number;
  /** En mobile, mostrar back button para volver a la lista. */
  onBack?: () => void;
}

export function ConversationDetail({ botId, sessionId, onBack }: Props): React.ReactElement {
  const { data, isLoading, isError } = useConversation(botId, sessionId);
  const [replayMode, setReplayMode] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);

  useConversationRealtime(botId, sessionId, data?.session.userPhone ?? null);

  const selectionsByMessageId = useMemo(() => {
    const map = new Map<string, string>();
    if (!data) return map;
    for (let i = 0; i < data.messages.length; i++) {
      const sel = findReplySelection(data.messages, i);
      if (sel) map.set(sel.sourceMessageId, sel.selectedId);
    }
    return map;
  }, [data]);

  // Auto-scroll al final cuando llegan mensajes nuevos, salvo que el usuario
  // haya scrolleado hacia arriba.
  // P-275: incluir `messages.length` en deps. Sin esto, cuando un mensaje
  // nuevo llega via WS y el user ya estaba al fondo (`isAtBottom = true`),
  // el effect no re-corre porque `isAtBottom` no cambió → scroll se queda
  // pegado y el user no ve el mensaje nuevo sin scrollear manualmente.
  const messageCount = data?.messages.length ?? 0;
  // `messageCount` se incluye en deps a propósito: queremos re-correr el
  // effect cuando llegan mensajes (aunque el body no lo lea) — sin él, el
  // scroll no se actualiza al recibir un message:* via WS.
  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll trigger by design
  useEffect(() => {
    if (!isAtBottom) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [isAtBottom, messageCount]);

  function handleScroll(e: React.UIEvent<HTMLDivElement>): void {
    const el = e.currentTarget;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    setIsAtBottom(distance < 32);
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Cargando conversación…
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-destructive">
        No se pudo cargar la conversación.
      </div>
    );
  }

  const display = deriveDisplayName(data.session);
  const messages: ConversationMessage[] = data.messages;

  if (replayMode) {
    return (
      <ReplayPlayer
        messages={messages}
        flowEvents={data.flowEvents}
        onExit={() => setReplayMode(false)}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          {onBack && (
            <Button
              size="icon"
              variant="ghost"
              onClick={onBack}
              aria-label="Volver a la lista"
              className="md:hidden"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <div className="min-w-0">
            <h2 className="truncate text-base font-semibold">{display.primary}</h2>
            {display.secondary && (
              <p className="truncate font-mono text-xs text-muted-foreground">
                {display.secondary}
              </p>
            )}
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setReplayMode(true)}
          disabled={messages.length === 0}
        >
          <Play className="h-4 w-4" /> Replay
        </Button>
      </header>

      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className={cn(
          'flex-1 space-y-2 overflow-y-auto p-4',
          messages.length === 0 && 'flex items-center justify-center',
        )}
      >
        {messages.length === 0 ? (
          <p className="text-sm text-muted-foreground">No hay mensajes en esta sesión.</p>
        ) : (
          messages.map((m) => (
            <MessageBubble
              key={m.id}
              message={m}
              selectedReplyId={selectionsByMessageId.get(m.id)}
            />
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="border-t border-border bg-muted/30 p-3 md:max-h-[40%] md:overflow-y-auto">
        <SessionPanel session={data.session} messageCount={messages.length} />
      </div>
    </div>
  );
}
